import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  IndianRupee,
  UserCircle,
  LogOut,
  Truck,
  Bell,
  ChevronDown,
  User,
  Wifi,
  WifiOff,
} from "lucide-react";
import PanelLayout from "@/components/shared/panel/PanelLayout";
import NotificationBell from "@/components/shared/notifications/NotificationBell";
import { useLogout } from "@/hooks/useLogout";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  useDeliveryDashboard,
  useDeliveryPartnerId,
  useToggleDeliveryOnlineMutation,
} from "@/hooks/queries";
import { getPartnerInitials } from "@/lib/mappers/delivery-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

const pageTitles: Record<string, string> = {
  "/delivery": "Dashboard",
  "/delivery/orders": "My Deliveries",
  "/delivery/earnings": "Earnings",
  "/delivery/profile": "Profile",
  "/delivery/notifications": "Notifications",
};

const DeliveryLayout = () => {
  const location = useLocation();
  const { toast } = useToast();
  const { partnerId, profile } = useDeliveryPartnerId();
  const { data: stats } = useDeliveryDashboard(partnerId);
  const toggleOnlineMutation = useToggleDeliveryOnlineMutation(partnerId ?? 0);

  const isOnline = profile?.isOnline ?? false;
  const activeOrders = stats?.pendingDeliveries ?? 0;
  const currentPageTitle = pageTitles[location.pathname] || "Delivery";
  const initials = profile ? getPartnerInitials(profile.name) : "DP";
  const logout = useLogout();

  const navItems = [
    { title: "Dashboard", url: "/delivery", icon: LayoutDashboard },
    { title: "My Deliveries", url: "/delivery/orders", icon: ShoppingCart, badge: activeOrders || undefined },
    { title: "Earnings", url: "/delivery/earnings", icon: IndianRupee },
    { title: "Profile", url: "/delivery/profile", icon: UserCircle },
    { title: "Notifications", url: "/delivery/notifications", icon: Bell },
  ];

  const handleToggleOnline = async () => {
    if (!partnerId) return;
    try {
      await toggleOnlineMutation.mutateAsync(!isOnline);
      toast({ title: isOnline ? "You are now offline" : "You are now online" });
    } catch (error) {
      toast({
        title: "Failed to update status",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const topbarActions = (
    <>
      <Button
        variant={isOnline ? "default" : "secondary"}
        size="sm"
        className="glass-btn gap-1.5 rounded-xl text-xs h-9"
        onClick={handleToggleOnline}
        disabled={!partnerId || toggleOnlineMutation.isPending}
      >
        {isOnline ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
        <span className="hidden sm:inline">{isOnline ? "Online" : "Offline"}</span>
      </Button>
      <NotificationBell scope="me" viewAllPath="/delivery/notifications" />
      <Separator orientation="vertical" className="h-7 hidden sm:block mx-0.5" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="glass-chip h-10 gap-2 rounded-xl px-1.5 hover:bg-transparent sm:px-2.5">
            <div className="relative">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-accent text-accent-foreground text-xs font-bold ring-2 ring-accent/20">
                {initials}
              </div>
              <span
                className={cn(
                  "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background dark:border-black",
                  isOnline ? "bg-success" : "bg-muted-foreground"
                )}
              />
            </div>
            <div className="hidden lg:flex flex-col items-start leading-tight">
              <span className="text-[13px] font-semibold text-foreground">{profile?.name ?? "Delivery Partner"}</span>
              <span className="text-[10px] text-muted-foreground">Delivery Partner</span>
            </div>
            <ChevronDown className="h-3 w-3 text-muted-foreground hidden sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} collisionPadding={8} className="w-[min(220px,calc(100vw-1rem))] bg-popover border border-border shadow-2xl z-50 rounded-xl p-1.5">
          <div className="px-2.5 py-3 mb-1 rounded-lg bg-secondary/40">
            <p className="text-sm font-bold text-foreground">{profile?.name ?? "Delivery Partner"}</p>
            <p className="text-[11px] text-muted-foreground">{profile?.email || profile?.phone || "—"}</p>
          </div>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
            <Link to="/delivery/profile">
              <User className="h-4 w-4 text-muted-foreground" /> My Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg text-sm py-2" asChild>
            <Link to="/delivery/earnings">
              <IndianRupee className="h-4 w-4 text-muted-foreground" /> Earnings
            </Link>
          </DropdownMenuItem>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                className="cursor-pointer gap-2.5 rounded-lg text-sm py-2 text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => logout()}
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <PanelLayout
      brand={{
        icon: Truck,
        iconClassName: "bg-gradient-accent",
        iconIconClassName: "text-accent-foreground",
        title: "Delivery",
        subtitle: profile?.name ?? "Partner Panel",
        url: "/delivery",
      }}
      navItems={navItems}
      footer={{ label: "Sign Out", url: "/" }}
      breadcrumbPrefix="Delivery"
      pageTitle={currentPageTitle}
      sectionLabel="MENU"
      topbarActions={topbarActions}
    />
  );
};

export default DeliveryLayout;
